// M004-F0744 sections 3.1 and 4: the envelope sidebar. First row = the always-present "New envelope" input (type, Enter; no modal, no
// second step). Rows: label, receipt count, running total in the envelope's currency, a state chip when not open, a padlock when
// locked. Unsorted is pinned first. On phones (< 720 px) the same list becomes a horizontal chip row (CSS only).
//
// M004-F0745 (design section 6.1/6.5): every row is a DROP TARGET for dragged receipts. While a drag is over a row that can take them it
// is highlighted with "Drop to move to <name>"; over a row that cannot (submitted, closed, deleted, still being created, offline) there
// is no highlight, the cursor is not-allowed and the row says why; the envelope the receipts already live in is inert. After 600 ms
// near the edge of a long list the list scrolls by itself.
import React, { useEffect, useRef, useState } from 'react';
import { MAX_ENVELOPE_NAME, UNSORTED_LABEL, describeTotals, isUnsorted, rowKey, stateChipFor, totalsFor } from './envelopeWorkspaceModel';
import { statusChip } from './envelopeModel';
import { needsAttention } from './claimModel';

const LockIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V6H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-.5V4.5A3.5 3.5 0 0 0 8 1Zm-2 3.5a2 2 0 1 1 4 0V6H6V4.5Z" /></svg>
);

const EDGE = 44;
const SCROLL_AFTER_MS = 600;
const SCROLL_STEP = 14;

// Counts glide: the number bumps once when it changes (never on first paint).
function useBump(value) {
  const previous = useRef(value);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (previous.current !== value) { previous.current = value; setBump((n) => n + 1); }
  }, [value]);
  return bump;
}

function Row({ record, selected, details, onSelect, onRestore, readOnly, drop }) {
  const id = record.envelope_id;
  const chip = stateChipFor(record) || (record.status === 'deleted' ? statusChip(record) : null);
  const totals = totalsFor(record, details?.status === 'ready' ? details.receipts : undefined);
  const money = describeTotals(totals);
  const count = totals ? totals.count : record.counts?.receipts ?? 0;
  const label = isUnsorted(record) ? UNSORTED_LABEL : record.name;
  const bump = useBump(count);
  const dropState = drop.state; // 'ok' | 'blocked' | 'same' | null
  const showHint = dropState === 'ok' || dropState === 'blocked';
  return (
    <li
      className={`ew-row${selected ? ' ew-row-selected' : ''}${record.locked ? ' ew-row-locked' : ''}${record.status === 'deleted' ? ' ew-row-deleted' : ''}${dropState === 'ok' ? ' ew-row-drop' : ''}${drop.dragging && drop.blocked ? ' ew-row-nodrop' : ''}`}
      data-testid={`envelopes-row-${id}`}
      data-pending={record.pending ? 'true' : undefined}
      data-kind={record.kind}
      data-drop={showHint ? dropState : undefined}
      onDragEnter={(event) => drop.onOver(event, record)}
      onDragOver={(event) => drop.onOver(event, record)}
      onDragLeave={(event) => drop.onLeave(event, record)}
      onDrop={(event) => drop.onDrop(event, record)}
    >
      <button type="button" className="ew-row-main" aria-current={selected ? 'true' : undefined} onClick={() => onSelect(record)}>
        <span className="ew-row-name">
          <strong>{label}</strong>
          {record.locked ? <span className="ew-lock" data-testid={`envelopes-row-lock-${id}`} role="img" aria-label="Locked"><LockIcon /></span> : null}
        </span>
        <span className="ew-row-meta">
          <span key={bump} className={bump ? 'ew-count-bump' : undefined} data-testid={`envelopes-row-count-${id}`}>{count}<span className="ew-visually-hidden"> receipts</span></span>
          {money.length === 1 ? <span className="ew-row-total" data-testid={`envelopes-row-total-${id}`}>{money[0]}</span> : null}
          {money.length > 1 ? <span className="ew-row-total" data-testid={`envelopes-row-total-${id}`}>{money.length} currencies</span> : null}
        </span>
        {record.pending ? <span className="ew-creating" data-testid="envelopes-row-creating">Creating…</span> : null}
        {chip ? <span className={`ew-chip ew-chip-${chip.tone}`} data-testid={`envelopes-chip-${id}`}>{chip.label}</span> : null}
        {needsAttention(record) ? <span className="ew-attn" data-testid={`envelopes-row-badge-${id}`}>Needs attention</span> : null}
      </button>
      {showHint ? (
        <span className="ew-drop-hint" data-testid={`envelopes-row-hint-${id}`} aria-hidden="true">
          {dropState === 'ok' ? `Drop to move to ${label}` : drop.reason}
        </span>
      ) : null}
      {record.status === 'deleted' ? (
        <button type="button" className="ew-btn ew-row-restore" disabled={readOnly} data-testid={`envelopes-restore-${id}`} onClick={() => onRestore(record)}>Restore</button>
      ) : null}
    </li>
  );
}

const NO_DROP = { state: null, reason: '', blocked: false, dragging: false, onOver: () => {}, onLeave: () => {}, onDrop: () => {} };

export default function EnvelopeSidebar({
  envelopes, selectedKey, detailsById, loading, inputRef, text, onText, onSubmit, onClear, createError, readOnlyReason, onSelect, onRestore,
  purge, dropFor, dragging,
}) {
  const disabled = Boolean(readOnlyReason);
  const navRef = useRef(null);
  const pointer = useRef(null);

  // Auto-scroll while a drag hovers an edge of the list for 600 ms (the list scrolls down in the sidebar, sideways in the phone chip row).
  useEffect(() => {
    const nav = navRef.current;
    if (!dragging || !nav) return undefined;
    const zoneOf = (event) => {
      const list = nav.querySelector('.ew-list');
      const box = nav.getBoundingClientRect();
      const horizontal = list && list.scrollWidth > list.clientWidth + 1 && getComputedStyle(list).display === 'flex';
      if (horizontal) {
        const listBox = list.getBoundingClientRect();
        if (event.clientX > listBox.right - EDGE) return 'right';
        if (event.clientX < listBox.left + EDGE) return 'left';
        return '';
      }
      if (event.clientY > box.bottom - EDGE) return 'down';
      if (event.clientY < box.top + EDGE) return 'up';
      return '';
    };
    const onOver = (event) => {
      const now = Date.now();
      const prior = pointer.current;
      pointer.current = { x: event.clientX, y: event.clientY, since: prior && prior.zone === zoneOf(event) ? prior.since : now, zone: zoneOf(event) };
    };
    nav.addEventListener('dragover', onOver);
    const timer = setInterval(() => {
      const last = pointer.current;
      if (!last || !last.zone || Date.now() - last.since < SCROLL_AFTER_MS) return;
      const list = nav.querySelector('.ew-list');
      if (last.zone === 'down') nav.scrollTop += SCROLL_STEP;
      if (last.zone === 'up') nav.scrollTop -= SCROLL_STEP;
      if (last.zone === 'right' && list) list.scrollLeft += SCROLL_STEP;
      if (last.zone === 'left' && list) list.scrollLeft -= SCROLL_STEP;
    }, 30);
    return () => { nav.removeEventListener('dragover', onOver); clearInterval(timer); pointer.current = null; };
  }, [dragging]);

  return (
    <nav className={`ew-sidebar${dragging ? ' ew-sidebar-dragging' : ''}`} aria-label="Envelopes" data-testid="envelopes-sidebar" ref={navRef}>
      <form
        className="ew-new"
        onSubmit={(event) => { event.preventDefault(); onSubmit(); }}
      >
        <label htmlFor="ew-new-name" className="ew-new-label">New envelope</label>
        <div className="ew-new-field">
          <input
            id="ew-new-name"
            ref={inputRef}
            value={text}
            maxLength={MAX_ENVELOPE_NAME}
            disabled={disabled}
            autoComplete="off"
            placeholder="Name it, press Enter  (N)"
            aria-describedby={`${disabled ? 'ew-new-reason ' : ''}${createError ? 'ew-new-error' : ''}`.trim() || undefined}
            aria-keyshortcuts="N"
            data-testid="envelopes-new-name"
            onChange={(event) => onText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); onSubmit(); }
              if (event.key === 'Escape') { event.stopPropagation(); onClear(); }
            }}
          />
          <button type="submit" className="ew-btn ew-btn-primary" disabled={disabled || !text.trim()} data-testid="envelopes-new">Create</button>
        </div>
        {disabled ? <p id="ew-new-reason" className="ew-muted ew-hint">{readOnlyReason}</p> : null}
        {createError ? (
          <p id="ew-new-error" className="ew-error ew-inline-error" role="alert" data-testid="envelopes-create-error">
            {createError.message}
            {createError.code ? <> <code data-testid="envelopes-create-error-code">{createError.code}</code></> : null}
          </p>
        ) : null}
      </form>

      {loading ? (
        <ul className="ew-list" data-testid="envelopes-skeleton" aria-hidden="true">
          {[0, 1, 2, 3].map((n) => <li key={n} className="ew-row ew-row-skel"><span className="ew-skel ew-skel-line" /><span className="ew-skel ew-skel-line ew-skel-short" /></li>)}
        </ul>
      ) : (
        <ul className="ew-list" data-testid="envelopes-list">
          {envelopes.map((record) => (
            <Row
              key={rowKey(record)}
              record={record}
              selected={rowKey(record) === selectedKey}
              details={detailsById[record.envelope_id]}
              readOnly={disabled}
              drop={dropFor ? dropFor(record) : NO_DROP}
              onSelect={onSelect}
              onRestore={onRestore}
            />
          ))}
        </ul>
      )}
      {purge}
    </nav>
  );
}
